import 'whatwg-fetch'

import { Storage } from 'aws-amplify'
import get from 'lodash/get'
import has from 'lodash/has'
import isNil from 'lodash/isNil'
import memoize from 'lodash/memoize'
import OLFormatGeoJSON from 'ol/format/GeoJSON'
import { all as OLLoadingStrategyAll } from 'ol/loadingstrategy'
import OLSourceVector from 'ol/source/Vector'

import PoziAuthAWS from 'aws/auth'
import LoaderHandleGeoJSON, { LoaderFailed } from 'config/LoaderHandleGeoJSON'

const configLocation = import.meta.env.VITE_CONFIG_LOCATION

/**
 * AWS Auth loader
 */
const PoziStorageAWS = memoize(function () {
  // Adapters
  const adapters = {
    /**
     * @api {NA} PoziStorageAWS().init Initialise
     * @apiDescription Pozi wrapper of Amplify storage components
     * @apiGroup AWSStorage
     * @apiName init
     */
    init: function () {
      return init()
    },
    /**
     * @api {NA} PoziStorageAWS().get Get a file
     * @apiDescription Component to get a file from AWS S3.
     * @apiGroup AWSStorage
     * @apiName get
     *
     * @apiParam {String} level public | protected | private
     * @apiParam {String} filename The name of the file
     *
     * @apiHeader (Amplify) {Link} Storage https://aws.github.io/aws-amplify/media/storage_guide
     */
    _get: function (level, filename) {
      return _get(level, filename)
    },
    /**
     * @api {NA} PoziStorageAWS().loader Get a file
     * @apiDescription Component to get a file from AWS S3.
     * @apiGroup AWSStorage
     * @apiName loader
     *
     * @apiParam {String} layer Pozi Layer
     */
    loader: function (layer) {
      return loader(layer)
    }
  }

  /**
   * Init
   */
  var init = function () {}

  var loader = function (layer) {
    if (isNil(layer)) {
      console.error('Invalid AWSS3 layer.')
      return null
    }

    if (!has(layer, 'titleid')) {
      console.error('No titleid on AWSS3 layer.')
      return null
    }

    if (!has(layer, 'config.spatial')) {
      console.error('Cannot load AWSS3 layer without spatial options.')
      return null
    }

    const loaderType = get(layer, 'config.spatial.loader', '')
    if (loaderType !== 'awss3') {
      console.error('Invalid layer loader type for AWSS3.')
      return null
    }

    const options = {
      format: new OLFormatGeoJSON(),
      crossOrigin: 'Anonymous',
      loader: function (extent, resolution, projection, success, failure) {
        const level = get(layer, 'config.spatial.storage.level', 'public') // default to public
        const filename = get(layer, 'config.spatial.storage.filename', null)
        if (isNil(filename)) {
          const errorMsg = 'Unable to find AWSS3 filename. Expecting layer.config.spatial.filename'
          console.error(errorMsg)

          LoaderFailed(layer, errorMsg, failure)

          return
        }
        _get(level, filename)
          .then(function (geojson) {
            LoaderHandleGeoJSON(geojson, projection, olSourceVector, layer, success)
            // TODO: Check if feature directive is in URL.. if so, highlight it. feature[layername,layerid]
          })
          .catch(function (err) {
            LoaderFailed(layer, err, failure)
          })
      },
      strategy: OLLoadingStrategyAll,
      projection: 'EPSG:3857'
    }

    const olSourceVector = new OLSourceVector(options)

    return olSourceVector
  }

  var _get = async function (level, filename) {
    const bSignedIn = PoziAuthAWS().signedIn()
    if (bSignedIn) {
      console.log('User is signed in... attempt to get s3 file.')
      Storage.configure({ level })
      const url = await Storage.get(filename).catch()
      if (isNil(url)) {
        return null
      }
      const response = await window.fetch(url).catch()
      if (isNil(response)) {
        console.log('PoziStorageAWS.get(): error getting response.')
        return null
      }
      const data = await response.json()
      console.log('PoziStorageAWS.get(): data.', data)
      return data
    }
  }

  // Return adapters (must be at end of adapter)
  return adapters
})

export async function putJSON (level, filename, data) {
  const isSignedIn = PoziAuthAWS().signedIn()
  if (!isSignedIn) {
    throw new Error('Storage.put user is not signed in')
  }

  // TODO where should this config be stored?
  Storage.configure({
    bucket: configLocation,
    region: 'ap-southeast-2'
  })

  const result = await Storage.put(filename, JSON.stringify(data, null, 2), {
    level,
    contentType: 'application/json'
  }).catch()

  if (isNil(result.key)) {
    console.error('Upload result: ', result)
    throw new Error('Failed to upload config')
  }

  return result.key
}

export async function put ({
  filename,
  data,
  onProgress,
  level = 'public'
}) {
  const isSignedIn = PoziAuthAWS().signedIn()
  if (!isSignedIn) {
    throw new Error('Storage.put user is not signed in')
  }

  Storage.configure({
    bucket: configLocation,
    region: 'ap-southeast-2'
  })

  const result = await Storage.put(filename, data, {
    level,
    progressCallback: onProgress
  }).catch()

  if (isNil(result.key)) {
    console.error('Upload result: ', result)
    throw new Error('Failed to upload config')
  }

  return result.key
}

export async function list (level, folder) {
  const bSignedIn = PoziAuthAWS().signedIn()
  if (!bSignedIn) {
    throw new Error('Cannot contact S3 when not signed in')
  }

  const pageSize = 1000 // AWS Amplify 5.x seems to require this argument to be provided (value between 0 and 1000)
  const response = await Storage.list(folder, { level, pageSize })
  const { results } = response

  // the first element returned is always the folder, remove it
  results.splice(0, 1)

  // add the filename property
  return results.map(f => ({
    ...f,
    filename: f.key.replace(`${folder}/`, '')
  }))
}

export async function getJSON (level, filename) {
  const bSignedIn = PoziAuthAWS().signedIn()
  if (bSignedIn) {
    // console.debug('User is signed in... attempt to get s3 file.')
    Storage.configure({ level })
    const url = await Storage.get(filename).catch()
    if (isNil(url)) {
      return null
    }
    const response = await window.fetch(url).catch()
    if (isNil(response)) {
      console.log('PoziStorageAWS.get(): error getting response.')
      return null
    }
    const data = await response.json()
    // console.debug('PoziStorageAWS.get(): data.', data)
    return data
  }
}

export async function getObjectURLImage (level, filename) {
  const bSignedIn = PoziAuthAWS().signedIn()
  if (bSignedIn) {
    console.log('User is signed in... attempt to get s3 file.')
    Storage.configure({ level })
    const url = await Storage.get(filename).catch()
    if (isNil(url)) {
      return null
    }
    const response = await window.fetch(url).catch()
    if (isNil(response)) {
      console.log('PoziStorageAWS.get(): error getting response.')
      return null
    }
    const blob = await response.blob()
    const objectURL = URL.createObjectURL(blob)
    return objectURL
  }
}

export default PoziStorageAWS
